/**
 * 除錯／遙測用的開關。
 *
 * 這裡的旗標刻意不走環境變數，直接以常數寫死：它們只在「要查某個問題」的期間才打開，
 * 開關本身就是這次調查的一部分，改動應該跟著 commit 一起被 review 與紀錄，
 * 而不是散落在各部署環境的設定裡、事後沒人知道現在到底是開還是關。
 * 要開啟時把值改成 true，查完再改回 false。
 */

/**
 * AI Web Game 遊戲 iframe 的 CSP 違規遙測總開關。
 *
 * 這是**上線相容性偵測，不是安全機制**：關掉只是少了觀測資料，CSP 本身照常強制，
 * 遊戲的行為、使用者流程完全不受影響。
 *
 * 關閉時 `reportCspViolation` 仍會 `console.error`（自己重現時看得到），但不送任何
 * Sentry 事件。之所以預設關閉：FEVER-ADMIN-2P6 累計 36,616 筆／7,257 人，平均每人 5 筆，
 * 量幾乎全部來自**人數**而非重複，會把 backend 真正的錯誤壓在噪音底下。
 *
 * 打開的時機：**客戶回報遊戲有異常**時。屆時把這個值改成 true 出一版，
 * 讓違規帶著 `sample`（違規程式碼前 40 字元）進 Sentry，查完再改回 false。
 * 節流與分組（sessionStorage 去重、抽樣、tags、fingerprint）都已就緒，
 * 打開後量級約為 50~100 筆／週，不會重演這次的爆量。
 *
 * 相關 issue：Sentry FEVER-ADMIN-2P6。
 */
export const IS_AI_WEB_GAME_CSP_VIOLATION_REPORT_ENABLED = false;

/**
 * AI Web Game 遊戲 iframe「載入慢／已自動重試」遙測的總開關（GameIframe.js），一個旗標管三支：
 * - Iframe-Load-Slow：bridge 已經在跑，只是子資源還沒載完，load 事件晚到（FEVER-ADMIN-2SB）。
 * - Iframe-Load-Fallback：blob iframe 沒觸發 load，自動改用 srcdoc 重試（FEVER-ADMIN-2SF）。
 * - Ready-Late：保底揭露後 GAME_READY 才到，遊戲有守契約、只是慢（FEVER-ADMIN-2R1）。
 *
 * 三者都是 warning 級，玩家流程不受影響，但量很大，會把真正的錯誤蓋掉。關閉時只是不送 Sentry：
 * srcdoc 重試、等待 load、保底揭露都照常運作，`console.warn` 也保留（自己重現時看得到）。
 * 玩家真的看到失敗畫面的 Iframe-Load-Error、違反契約的 Ready-Missing 不受這個開關影響，一律照報。
 *
 * 打開的時機：要調整 IFRAME_LOAD_TIMEOUT_MS／GAME_READY_TIMEOUT_MS，或客戶回報遊戲載入慢、
 * 需要看 load_mode、elapsed_bucket 等分布時。屆時改成 true 出一版，查完再改回 false。
 *
 * 相關 issue：Sentry FEVER-ADMIN-2SB、FEVER-ADMIN-2SF、FEVER-ADMIN-2R1。
 */
export const IS_AI_WEB_GAME_LOAD_TELEMETRY_ENABLED = false;

/**
 * 「載入失敗」頁（chunk load 失敗）強制回報的總開關。
 *
 * 這類錯誤預設會被 sentry.js 的 `beforeSend` 濾掉，ErrorBoundaryApp 以 `forceReport` 反過來強制送出，
 * 並額外打三個探測（chunk / asset-manifest / 本頁 HTML）來判斷成因，是純遙測、用量也大。
 *
 * 關閉時 chunk load 失敗回到 `beforeSend` 的預設過濾（完全不送 Sentry），探測也不會發，
 * 使用者仍看得到「載入失敗」頁與不含成因段的代碼（例：LF-CSS1570-E），只是查不到成因。
 * 相關 issue：Sentry FEVER-ADMIN-2QH。
 */
export const IS_CHUNK_LOAD_ERROR_REPORT_ENABLED = false;

/**
 * Init API 逾時監控（client/utils/initApiTimeoutMonitor.js）的總開關。
 *
 * 關閉時 createApiMonitor 回傳純轉發函數：不計時、不掛 page presence listener、
 * 不送任何 Sentry 事件。這支監控是純遙測，關掉不影響任何使用者流程。
 */
export const IS_INIT_API_TIMEOUT_MONITOR_ENABLED = false;
